/* Derives dashboard metrics and recommendations from real workspace records. */

export const parseJSON = value => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return null; }
};

export const scanFindings = scan => {
  const findings = parseJSON(scan?.findings);
  return Array.isArray(findings) ? findings : [];
};

export const failedFindings = scan => scanFindings(scan).filter(item => !item.passed);

export const setupSteps = optimization => [
  { key: 'gbp_connected', label: 'Google Business Profile access' },
  { key: 'facebook_connected', label: 'Facebook business access' },
  { key: 'media_uploaded', label: 'Brand and location media' },
  { key: 'tracking_ready', label: 'Rank tracking configured' },
].map(step => ({ ...step, done: Boolean(optimization?.[step.key]) }));

export const generationsThisMonth = generations => {
  const now = new Date();
  return (generations || []).filter(item => {
    const created = new Date(item.created_at);
    return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
  }).length;
};

const rankValues = keywords => (keywords || [])
  .map(item => item.maps_rank || item.search_rank)
  .filter(value => Number.isFinite(Number(value)) && Number(value) > 0)
  .map(Number);

export function buildMetrics(workspace) {
  const scans = workspace.scans || [];
  const keywords = workspace.keywords || [];
  const prospects = workspace.prospects || [];
  const ranks = rankValues(keywords);

  const latestScore = scans.length ? Number(scans[0].score) : null;
  const previousScore = scans.length > 1 ? Number(scans[1].score) : null;
  const openLeads = prospects.filter(lead => ['new', 'contacted', 'qualified'].includes(lead.status)).length;
  const newLeads = prospects.filter(lead => lead.status === 'new').length;
  const averageRank = ranks.length ? Math.round(ranks.reduce((sum, value) => sum + value, 0) / ranks.length) : null;
  const topThree = ranks.filter(value => value <= 3).length;
  const used = generationsThisMonth(workspace.generations);

  return {
    visibility: {
      value: latestScore === null ? '—' : latestScore,
      unit: latestScore === null ? '' : '/100',
      change: latestScore !== null && previousScore !== null ? latestScore - previousScore : null,
      footer: scans.length ? `Last scan ${new Date(scans[0].created_at).toLocaleDateString()}` : 'No scan yet',
    },
    leads: {
      value: prospects.length,
      footer: prospects.length ? `${openLeads} open · ${newLeads} new` : 'No leads yet',
    },
    rank: {
      value: averageRank === null ? '—' : averageRank,
      footer: keywords.length ? `${keywords.length} tracked · ${topThree} in top 3` : 'No keywords tracked',
    },
    ai: {
      value: used,
      unit: `/ ${workspace.limits?.aiGenerations ?? 25}`,
      footer: 'Drafts created this month',
    },
  };
}

export function buildRecommendations(workspace) {
  const items = [];
  const scans = workspace.scans || [];
  const prospects = workspace.prospects || [];
  const keywords = workspace.keywords || [];
  const records = workspace.records || [];
  const steps = setupSteps(workspace.optimization);
  const incomplete = steps.filter(step => !step.done);

  if (!scans.length) {
    items.push({
      id: 'first-scan', priority: 'high', title: 'Run your first visibility scan',
      description: 'Check how your website looks to local customers and search engines before spending on marketing.',
      cta: { label: 'Run scan', to: '/dashboard/visibility' },
    });
  } else {
    const failing = failedFindings(scans[0]);
    if (failing.length) {
      items.push({
        id: 'fix-visibility', priority: 'high', title: `Fix ${failing.length} website ${failing.length === 1 ? 'issue' : 'issues'}`,
        description: `Your last scan of ${scans[0].business || 'your website'} found ${failing.length} missing local-search ${failing.length === 1 ? 'signal' : 'signals'}.`,
        cta: { label: 'Fix visibility', to: '/dashboard/visibility' },
      });
    }
  }

  const newLeads = prospects.filter(lead => lead.status === 'new');
  if (newLeads.length) {
    items.push({
      id: 'follow-up', priority: 'high', title: `Follow up with ${newLeads.length} new ${newLeads.length === 1 ? 'lead' : 'leads'}`,
      description: 'Leads contacted quickly are far more likely to convert. Draft a reply and move them forward.',
      cta: { label: 'View leads', to: '/dashboard/leads' },
    });
  }

  const overdue = records.filter(record => record.due_at && new Date(record.due_at) < new Date() && !['complete', 'archived'].includes(record.status));
  if (overdue.length) {
    items.push({
      id: 'overdue', priority: 'high', title: `${overdue.length} ${overdue.length === 1 ? 'item is' : 'items are'} past due`,
      description: 'Work in your operations queue passed its due date and still needs attention.',
      cta: { label: 'Open operations', to: '/dashboard/operations' },
    });
  }

  const slipping = keywords.filter(item => {
    const best = item.maps_rank || item.search_rank;
    return best && Number(best) > 10;
  });
  if (slipping.length) {
    items.push({
      id: 'rank-drop', priority: 'medium', title: `${slipping.length} ${slipping.length === 1 ? 'keyword sits' : 'keywords sit'} outside the top 10`,
      description: 'Customers rarely look past the first page. A focused local page can improve these positions.',
      cta: { label: 'Improve ranking', to: '/dashboard/ai-studio/seo_page_writer' },
    });
  }

  if (incomplete.length) {
    items.push({
      id: 'setup', priority: 'medium', title: `Finish setup (${steps.length - incomplete.length} of ${steps.length} complete)`,
      description: `Connect ${incomplete[0].label} so tracking and recommendations stay accurate.`,
      cta: { label: 'Continue setup', to: '/dashboard/setup' },
    });
  }

  if (!(workspace.generations || []).length) {
    items.push({
      id: 'first-draft', priority: 'low', title: 'Create your first AI draft',
      description: 'Turn your business details into a website, campaign, or follow-up message you can edit and publish.',
      cta: { label: 'Open AI Studio', to: '/dashboard/ai-studio' },
    });
  }

  const order = { high: 0, medium: 1, low: 2 };
  return items.sort((a, b) => order[a.priority] - order[b.priority]);
}
